import { API_URL } from '../config';
import { Team } from '../models/Tournament';
import { UserRole } from '../utils/ApiKeyData';
import { buildHeaders } from './headers';

interface Credential {
  username: string;
  password: string;
  tenantId: number;
}

export interface User {
  id: number;
  login?: string;
  role: UserRole;
  tenantId: number;
  name?: string;
  surname?: string;
  phoneNumber?: string;
  regulationsConsent?: boolean;
  gdprConsent?: boolean;
  marketingConsent?: boolean;
  ownedSoccerTeams?: Team[];
}

export const userLogin = async (login: string, password: string, tenantId: number): Promise<string> => {
  const creds: Credential = {
    username: login,
    password: password,
    tenantId: tenantId,
  };
  const response = await fetch(`${API_URL}/login`, {
    method: 'POST',
    body: JSON.stringify(creds),
    headers: { 'Content-Type': 'application/json' },
  }).catch((error) => {
    return Promise.reject(error);
  });
  if (response.ok) {
    const data = await response.json();
    return data;
  } else {
    return Promise.reject(response);
  }
};

export const userLogout = async (apiKey: string) => {
  try {
    const response = await fetch(`${API_URL}/logout?apiKey=${apiKey}`, {
      method: 'POST',
      headers: buildHeaders(apiKey),
    });
    if (response.ok) {
      return response.ok;
    } else {
      return Promise.reject(response);
    }
  } catch (error) {
    return Promise.reject();
  }
};

export const getUserProfile = async (apiKey: string): Promise<User> => {
  try {
    const response = await fetch(`${API_URL}/profile`, {
      headers: buildHeaders(apiKey),
    });
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      return Promise.reject(response);
    }
  } catch (error) {
    return Promise.reject();
  }
};

export const resetPassword = async (login: string, tenantId: number) => {
  try {
    const response = await fetch(`${API_URL}/request-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: login, tenantId: tenantId }),
    });
    if (response.ok) {
      return true;
    } else {
      return Promise.reject(response);
    }
  } catch (error) {
    return Promise.reject();
  }
};

export const confirmPasswordReset = async (token: string, newPassword: string): Promise<void> => {
  const response = await fetch(`${API_URL}/confirm-password-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, newPassword }),
  });
  if (!response.ok) {
    throw new Error('Password reset failed');
  }
};

export const getUsers = async (apiKey: string): Promise<User[]> => {
  try {
    const response = await fetch(`${API_URL}/users`, {
      headers: buildHeaders(apiKey),
    });
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      return Promise.reject(response);
    }
  } catch (error) {
    return Promise.reject();
  }
};

export const registerUser = async (
  apiKey: string,
  user: { login: string; password: string; role: UserRole; tenantId: number },
): Promise<void> => {
  const body = {
    username: user.login,
    password: user.password,
    role: user.role,
    tenantId: user.tenantId,
  };

  const response = await fetch(`${API_URL}/register`, {
    method: 'POST',
    headers: buildHeaders(apiKey),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Failed to register user: ${response.statusText}`);
  }
};

export const resetUserPassword = async (apiKey: string, userId: number, newPassword: string): Promise<void> => {
  const body = {
    userId,
    newPassword,
  };

  const response = await fetch(`${API_URL}/reset-password`, {
    method: 'POST',
    headers: buildHeaders(apiKey),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Failed to reset password: ${response.statusText}`);
  }
};
